import { cva, type VariantProps } from "class-variance-authority";
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/cn";

const alertVariants = cva(
  "flex gap-3 border px-3 py-2 text-sm",
  {
    variants: {
      tone: {
        info:    "border-info/40 text-foreground",
        success: "border-success/40 text-foreground",
        warning: "border-warning/40 text-foreground",
        danger:  "border-danger/40 text-foreground",
      },
    },
    defaultVariants: { tone: "info" },
  },
);

const iconForTone = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
};

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  title?: string;
}

export const Alert = ({ className, tone = "info", title, children, ...props }: AlertProps) => {
  const Icon = iconForTone[tone ?? "info"];
  return (
    <div className={cn(alertVariants({ tone }), className)} role="alert" {...props}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.5} />
      <div className="space-y-1">
        {title && <div className="font-medium leading-tight">{title}</div>}
        <div className="text-muted-foreground">{children}</div>
      </div>
    </div>
  );
};
